import { supabase, getCurrentUserId, isSupabaseConfigured } from "@/lib/supabase";
import { isSongProductionThread } from "@/lib/isSongProductionThread";

const TABLE = "SongCreationSources";
const DOMAIN = "song_production";

type ChatMessage = { role?: string; content?: string };

function stripHtml(html: string): string {
  return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
}

function lastAssistantPreview(messages: ChatMessage[] | undefined): string {
  if (!Array.isArray(messages)) return "";
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === "assistant" && typeof m.content === "string") {
      return stripHtml(m.content).slice(0, 300);
    }
  }
  return "";
}

export function getSongCreationAdminUrl(bridgeId: string): string {
  const base =
    import.meta.env.VITE_PRAYERLIFE_ADMIN_URL?.trim() ||
    import.meta.env.VITE_SONG_CREATION_ADMIN_URL?.trim() ||
    "http://localhost:5001";
  const normalized = base.replace(/\/$/, "");
  return `${normalized}/life-music/song-creation?source_id=${encodeURIComponent(bridgeId)}`;
}

export type PushToSongCreatorParams = {
  threadId: string | number;
  title?: string | null;
  messages?: ChatMessage[];
  sourceType?: "thread" | "document";
  documentId?: string;
};

export async function pushToSongCreator(
  params: PushToSongCreatorParams
): Promise<{ bridgeId: string; adminUrl: string }> {
  if (!isSupabaseConfigured) {
    throw new Error("Database is not configured.");
  }

  const userId = await getCurrentUserId();
  if (!userId) {
    throw new Error("Please sign in to send to Song Creator.");
  }

  const sourceType = params.sourceType ?? "thread";
  const sourceId =
    sourceType === "document"
      ? String(params.documentId ?? "").trim()
      : String(params.threadId);

  if (!sourceId) {
    throw new Error("Missing thread or document id.");
  }

  const title = params.title?.trim() || "";
  if (sourceType === "thread" && !isSongProductionThread(title)) {
    throw new Error(
      "This chat does not look like a song production thread. Use a SONG / Suno / lyrics thread first."
    );
  }

  const preview = lastAssistantPreview(params.messages);

  const { data, error } = await supabase
    .from(TABLE)
    .upsert(
      {
        domain: DOMAIN,
        pushedFrom: "deeply_smart",
        sourceType,
        sourceId,
        title: title || (sourceType === "thread" ? `Thread ${sourceId}` : `Document ${sourceId}`),
        preview,
        status: "ready",
        createdByUserId: userId,
      },
      { onConflict: "domain,sourceType,sourceId" }
    )
    .select("id")
    .single();

  if (error) {
    throw new Error(error.message || "Failed to push to Song Creator");
  }

  const bridgeId = data?.id as string;
  if (!bridgeId) {
    throw new Error("Failed to create song creation handoff row.");
  }

  return { bridgeId, adminUrl: getSongCreationAdminUrl(bridgeId) };
}
